import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { t } = useLang();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    let timer;
    const poll = async () => {
      attempts.current += 1;
      if (attempts.current > 12) { setStatus("timeout"); return; }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          await checkAuth();
          return;
        }
        if (["expired", "failed"].includes(res.data.status)) { setStatus("error"); return; }
      } catch (e) {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [checkAuth]);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center px-5">
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="relative glass rounded-3xl p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="animate-spin text-[#F5842B] mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-6">{t("payment.checking")}</h1>
            <p className="text-slate-400 mt-2 text-sm">{t("payment.checkingDesc")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-20 w-20 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto"><CheckCircle2 size={44} className="text-emerald-400" /></div>
            <h1 className="font-display text-2xl font-extrabold mt-6">{t("payment.paidTitle")}</h1>
            <p className="text-slate-400 mt-2 text-sm">{t("payment.paidDesc")}</p>
            <button onClick={() => navigate("/dashboard")} className="mt-8 w-full adj-gradient text-white font-semibold py-3.5 rounded-full hover:opacity-90 transition-opacity" data-testid="success-dashboard-btn">
              {t("payment.goDashboard")}
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <div className="h-20 w-20 rounded-full bg-red-500/15 flex items-center justify-center mx-auto"><XCircle size={44} className="text-red-400" /></div>
            <h1 className="font-display text-2xl font-bold mt-6">{status === "timeout" ? t("payment.timeoutTitle") : t("payment.errorTitle")}</h1>
            <p className="text-slate-400 mt-2 text-sm">{status === "timeout" ? t("payment.timeoutDesc") : t("payment.errorDesc")}</p>
            <button onClick={() => navigate("/dashboard")} className="mt-8 w-full glass text-white font-semibold py-3.5 rounded-full hover:border-[#F5842B]/50 transition-colors">
              {t("payment.goDashboard")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
